/**
 * Environment Variable Validation Module
 *
 * This module ensures all required environment variables are present and valid
 * before the application starts. This prevents runtime errors and security issues
 * from missing or invalid configuration.
 *
 * Security: Never use fallback values for security-critical environment variables.
 * Always fail fast if required variables are missing.
 */

export interface ValidationResult {
  isValid: boolean;
  missing: string[];
  errors: string[];
}

/**
 * List of required environment variables for the application
 */
const REQUIRED_ENV_VARS = [
  'VITE_SUPABASE_URL',
  'VITE_SUPABASE_PUBLISHABLE_KEY',
] as const;

/**
 * Validates all required environment variables
 *
 * @returns ValidationResult with validation status, missing variables, and errors
 */
export function validateEnvironment(): ValidationResult {
  const missing: string[] = [];
  const errors: string[] = [];

  // Check for missing or empty variables
  for (const varName of REQUIRED_ENV_VARS) {
    const value = import.meta.env[varName];

    if (!value || typeof value !== 'string' || value.trim() === '') {
      missing.push(varName);
    }
  }

  // Validate URL format for VITE_SUPABASE_URL (only if present)
  if (import.meta.env.VITE_SUPABASE_URL &&
      typeof import.meta.env.VITE_SUPABASE_URL === 'string' &&
      import.meta.env.VITE_SUPABASE_URL.trim() !== '') {
    try {
      const url = new URL(import.meta.env.VITE_SUPABASE_URL);

      // Validate protocol (should be http or https)
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        errors.push('VITE_SUPABASE_URL must use http:// or https:// protocol');
      }

      // Validate hostname exists
      if (!url.hostname) {
        errors.push('VITE_SUPABASE_URL must have a valid hostname');
      }
    } catch {
      errors.push('VITE_SUPABASE_URL must be a valid URL');
    }
  }

  // Validate publishable key format (basic check - should be non-empty string)
  if (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY &&
      typeof import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY === 'string' &&
      import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY.trim() !== '') {
    const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY.trim();

    // Basic validation: Supabase keys are typically long JWT-like strings
    // Allow shorter keys for testing purposes
    if (key.length < 8) {
      errors.push('VITE_SUPABASE_PUBLISHABLE_KEY appears to be invalid (too short)');
    }
  }

  return {
    isValid: missing.length === 0 && errors.length === 0,
    missing,
    errors,
  };
}

/**
 * Ensures the environment is valid, throwing an error if not
 *
 * This should be called during application startup to fail fast
 * if the environment is not properly configured.
 *
 * @throws Error if environment validation fails
 */
export function ensureValidEnvironment(): void {
  const result = validateEnvironment();

  if (!result.isValid) {
    const errorParts: string[] = [
      '❌ Environment validation failed:',
      '',
    ];

    if (result.missing.length > 0) {
      errorParts.push('  Missing variables:');
      result.missing.forEach(varName => {
        errorParts.push(`    - ${varName}`);
      });
      errorParts.push('');
    }

    if (result.errors.length > 0) {
      errorParts.push('  Validation errors:');
      result.errors.forEach(error => {
        errorParts.push(`    - ${error}`);
      });
      errorParts.push('');
    }

    errorParts.push('  Check your .env file and ensure all required variables are set.');
    errorParts.push('  Refer to .env.example.secure for the correct template.');

    throw new Error(errorParts.join('\n'));
  }

  console.log('✅ Environment validation passed');
}

/**
 * Gets an environment variable value with validation
 *
 * @param key Environment variable key
 * @returns The environment variable value
 * @throws Error if the variable is not set
 */
export function getEnvVar(key: string): string {
  const value = import.meta.env[key];

  if (!value || typeof value !== 'string' || value.trim() === '') {
    throw new Error(
      `Environment variable ${key} is required but not set. Check your .env file.`
    );
  }

  return value;
}

/**
 * Gets an optional environment variable value
 *
 * @param key Environment variable key
 * @param defaultValue Default value if not set
 * @returns The environment variable value or default
 */
export function getOptionalEnvVar(key: string, defaultValue: string): string {
  const value = import.meta.env[key];

  if (!value || typeof value !== 'string' || value.trim() === '') {
    return defaultValue;
  }

  return value;
}
